<script setup>
/* global axios */
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CreateTemplateModal from 'flow_builder/components/CreateTemplateModal.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();
const currentAccountId = useMapGetter('getCurrentAccountId');

const isSyncing = ref(false);
const isDeleting = ref(false);
const showCreateModal = ref(false);
const deleteDialogRef = ref(null);
const templateToDelete = ref(null);

const templates = computed(() => {
  return store.getters['inboxes/getWhatsAppTemplates'](props.inbox.id) || [];
});

const sortedTemplates = computed(() =>
  [...templates.value].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  )
);

const statusClass = status => {
  switch ((status || '').toUpperCase()) {
    case 'APPROVED':
      return 'bg-n-teal-3 text-n-teal-11';
    case 'PENDING':
    case 'IN_APPEAL':
      return 'bg-n-amber-3 text-n-amber-11';
    case 'REJECTED':
    case 'DISABLED':
    case 'PAUSED':
      return 'bg-n-ruby-3 text-n-ruby-11';
    default:
      return 'bg-n-alpha-2 text-n-slate-11';
  }
};

const statusLabel = status => {
  const key = (status || '').toUpperCase();
  return t(`INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.${key}`, key);
};

async function syncTemplates() {
  isSyncing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inbox.id);
    await store.dispatch('inboxes/get');
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNCED_SUCCESS'));
  } catch (e) {
    useAlert(e.message || t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC_FAILED'));
  } finally {
    isSyncing.value = false;
  }
}

function openDeleteConfirm(template) {
  templateToDelete.value = template;
  deleteDialogRef.value?.open();
}

async function confirmDelete() {
  if (!templateToDelete.value) return;
  isDeleting.value = true;
  try {
    await axios.delete(
      `/api/v1/accounts/${currentAccountId.value}/inboxes/${props.inbox.id}/whatsapp_templates/${encodeURIComponent(templateToDelete.value.name)}`
    );
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETED_SUCCESS'));
    await store.dispatch('inboxes/get');
    deleteDialogRef.value?.close();
  } catch (err) {
    useAlert(err.response?.data?.error || err.message);
  } finally {
    isDeleting.value = false;
    templateToDelete.value = null;
  }
}

async function onTemplateCreated() {
  showCreateModal.value = false;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inbox.id);
    await store.dispatch('inboxes/get');
  } catch (_e) {
    // template may be PENDING — list may take time to refresh
  }
}

const deleteDescription = computed(() =>
  templateToDelete.value
    ? t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM_DESCRIPTION', {
        name: templateToDelete.value.name,
      })
    : ''
);

onMounted(() => {
  if (!templates.value.length) syncTemplates();
});
</script>

<template>
  <div class="mx-6">
    <div class="flex items-start justify-between gap-3 mb-6">
      <div class="flex flex-col gap-1">
        <h3 class="text-heading-3 text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TITLE') }}
        </h3>
        <p class="text-body-small text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.SUBTITLE') }}
        </p>
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-refresh-cw"
          :is-loading="isSyncing"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC_BUTTON')"
          @click="syncTemplates"
        />
        <Button
          size="sm"
          icon="i-lucide-plus"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE_BUTTON')"
          @click="showCreateModal = true"
        />
      </div>
    </div>

    <div
      v-if="!sortedTemplates.length"
      class="flex flex-col items-center justify-center py-12 gap-2 text-n-slate-11 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
    >
      <span class="i-lucide-message-square-text size-8 text-n-slate-9" />
      <p class="text-body-main font-medium">
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY_TITLE') }}
      </p>
      <p class="text-body-small">
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY_DESCRIPTION') }}
      </p>
    </div>

    <div v-else class="w-full">
      <table
        class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
      >
        <thead>
          <tr class="border-b border-n-weak">
            <th
              class="py-3 ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4 text-start text-heading-3 text-n-slate-12"
            >
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.COL_NAME') }}
            </th>
            <th
              class="py-3 ltr:pr-3 rtl:pl-3 text-start text-heading-3 text-n-slate-12"
            >
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.COL_CATEGORY') }}
            </th>
            <th
              class="py-3 ltr:pr-3 rtl:pl-3 text-start text-heading-3 text-n-slate-12"
            >
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.COL_LANGUAGE') }}
            </th>
            <th
              class="py-3 ltr:pr-3 rtl:pl-3 text-start text-heading-3 text-n-slate-12"
            >
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.COL_STATUS') }}
            </th>
            <th class="w-12 py-3 ltr:pr-4 rtl:pl-4" />
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr
            v-for="template in sortedTemplates"
            :key="`${template.name}-${template.language}`"
            class="text-n-slate-12"
          >
            <td
              class="py-3 ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4 text-body-main font-medium"
            >
              {{ template.name }}
            </td>
            <td class="py-3 ltr:pr-3 rtl:pl-3 text-body-small text-n-slate-11">
              {{ template.category }}
            </td>
            <td class="py-3 ltr:pr-3 rtl:pl-3 text-body-small text-n-slate-11">
              {{ template.language }}
            </td>
            <td class="py-3 ltr:pr-3 rtl:pl-3">
              <span
                :class="statusClass(template.status)"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide"
              >
                {{ statusLabel(template.status) }}
              </span>
            </td>
            <td class="py-3 ltr:pr-4 rtl:pl-4 text-right">
              <Button
                variant="ghost"
                color="ruby"
                size="sm"
                icon="i-lucide-trash-2"
                :title="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_BUTTON')"
                @click="openDeleteConfirm(template)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <CreateTemplateModal
      :show="showCreateModal"
      :inbox-id="inbox.id"
      @close="showCreateModal = false"
      @created="onTemplateCreated"
    />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      width="sm"
      :title="
        templateToDelete
          ? $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM_TITLE', {
              name: templateToDelete.name,
            })
          : ''
      "
      :description="deleteDescription"
      :confirm-button-label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_BUTTON')"
      :is-loading="isDeleting"
      @confirm="confirmDelete"
    />
  </div>
</template>
